import React, { useState, useEffect, useRef, useCallback } from "react";
import "./XenStarAwards.css";

/* ─────────────────────────────────────────────
   STATIC DATA
───────────────────────────────────────────── */
const STARS = Array.from({ length: 140 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 2.4 + 0.6,
  delay: Math.random() * 6,
  duration: Math.random() * 4 + 2,
}));

const CONFETTI_COLORS = [
  "#61d811", "#ffffff", "#ffd700", "#a8f040",
  "#ffec3d", "#61d811", "#fff", "#d4ff80",
];

/* ─────────────────────────────────────────────
   CONFETTI ENGINE
───────────────────────────────────────────── */
function useConfetti() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const pieces    = useRef([]);

  const launch = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    pieces.current = Array.from({ length: 320 }, () => ({
      x:     Math.random() * canvas.width,
      y:     -20 - Math.random() * 220,
      vx:    (Math.random() - 0.5) * 6,
      vy:    2 + Math.random() * 5,
      rot:   Math.random() * 360,
      rotV:  (Math.random() - 0.5) * 9,
      w:     6 + Math.random() * 10,
      h:     3 + Math.random() * 5,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      alpha: 1,
    }));
    const ctx = canvas.getContext("2d");
    let frame = 0;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      let alive = 0;
      pieces.current.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.07; p.vx *= 0.99; p.rot += p.rotV;
        if (frame > 110) p.alpha -= 0.01;
        if (p.alpha <= 0) return;
        alive++;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (alive > 0) animRef.current = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);
  return { canvasRef, launch };
}

/* ─────────────────────────────────────────────
   LETTER-BY-LETTER TYPEWRITER
───────────────────────────────────────────── */
function useTypewriter(text, active, intervalMs = 140) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    if (!active) return;
    setDisplayed(0);
    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplayed(i);
      if (i >= text.length) clearInterval(id);
    }, intervalMs);
    return () => clearInterval(id);
  }, [text, active, intervalMs]);
  return displayed;
}

/* ─────────────────────────────────────────────
   BURST PARTICLES
───────────────────────────────────────────── */
function useBurst() {
  const [particles, setParticles] = useState([]);
  const burst = (cx, cy) => {
    const items = Array.from({ length: 40 }, (_, i) => {
      const angle = (i / 40) * Math.PI * 2;
      const speed = 50 + Math.random() * 90;
      return {
        id: Date.now() + i, x: cx, y: cy,
        vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        size: Math.random() * 9 + 4,
      };
    });
    setParticles(items);
    setTimeout(() => setParticles([]), 900);
  };
  return { particles, burst };
}

/* ─────────────────────────────────────────────
   XENOVEX LOGO
───────────────────────────────────────────── */
function XenovexLogo({ className = "" }) {
  return (
    <div className={`xen-brand-logo ${className}`}>
      <img
        src={`${process.env.PUBLIC_URL}/Xenovex Logo White.webp`}
        alt="Xenovex Technologies"
        className="xen-brand-img"
        draggable={false}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   XEN STAR HERO
───────────────────────────────────────────── */
function XenStarHero({ size = "large" }) {
  return (
    <div className={`xenstar-hero xenstar-hero--${size}`}>
      <div className="xenstar-hero__glow" />
      <div className="xenstar-hero__inner">
        <div className="xenstar-hero__img-wrap">
          <div className="xenstar-hero__ring xenstar-hero__ring--1" />
          <div className="xenstar-hero__ring xenstar-hero__ring--2" />
          <img
            src={`${process.env.PUBLIC_URL}/Xen Star 2026 Logo.webp`}
            alt="Xen Star 2026"
            className="xenstar-hero__img"
            draggable={false}
          />
        </div>
        <div className="xenstar-hero__text">
          <span className="xenstar-hero__title">XEN STAR</span>
          <span className="xenstar-hero__year">2026</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHARED NAV COMPONENTS
───────────────────────────────────────────── */
function BackBtn({ onClick }) {
  return (
    <button className="xen-back-btn"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label="Go back">
      <span className="xen-back-btn__arrow">←</span>
      <span className="xen-back-btn__text">BACK</span>
    </button>
  );
}

function ProgressDots({ current }) {
  return (
    <div className="xen-progress">
      {[1, 2].map((n) => (
        <div key={n} className={`xen-progress__dot${n <= current ? " xen-progress__dot--done" : ""}`} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SCREEN 0 — LANDING 
═══════════════════════════════════════════ */
const EVENT_NAME = "XEN STAR 2026";

function LandingScreen({ onStart }) {
  const [phase, setPhase] = useState("blackout");
  const [showSub, setShowSub] = useState(false);
  const lettersTyped = useTypewriter(EVENT_NAME, phase === "typewrite", 110);

  useEffect(() => {
    const t = setTimeout(() => setPhase("typewrite"), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase === "typewrite" && lettersTyped >= EVENT_NAME.length) {
      const t = setTimeout(() => { 
        setPhase("celebrate"); 
        setShowSub(true); 
      }, 500); 
      return () => clearTimeout(t);
    }
  }, [lettersTyped, phase]);

  const typing      = phase === "typewrite";
  const celebrating = phase === "celebrate";

  return (
    <div
      className={`fs-screen fs-landing-event has-bottom-panel ${phase === "blackout" ? "fs-landing-event--dark" : ""}`}
      onClick={celebrating ? onStart : undefined}
    >
      <XenovexLogo className="landing-logo" />

      <div className="event-curtain event-curtain--l" />
      <div className="event-curtain event-curtain--r" />
      <div className={`event-spotlight ${phase !== "blackout" ? "event-spotlight--on" : ""}`} />

      <div className="landing-hero">
        
        {/* Typewriter text - HIDDEN when celebrating */}
        {!celebrating && (
          <div className="landing-name-wrap">
            <h1 className="landing-event-name" aria-label={EVENT_NAME}>
              {EVENT_NAME.split("").map((ch, i) => (
                <span key={i} className={[
                  "event-letter",
                  i < lettersTyped ? "event-letter--on" : "",
                ].filter(Boolean).join(" ")}>
                  {ch === " " ? "\u00A0" : ch}
                </span>
              ))}
            </h1>
            {typing && lettersTyped < EVENT_NAME.length && (
              <span className="event-cursor">|</span>
            )}
          </div>
        )}

        {/* Logo and Content - SHOWN ONLY when celebrating */}
        {celebrating && (
          <>
            <p className="landing-eyebrow landing-eyebrow--visible fade-in-up">
              XENOVEX PROUDLY PRESENTS
            </p>

            <div className="landing-hero-logo fade-in-scale">
              <XenStarHero size="large" />
            </div>

            <div className="event-stars-row">
              {["✦","★","✦","★","✦","★","✦"].map((s, i) => (
                <span key={i} className="event-star-sep"
                  style={{ animationDelay: `${i * 0.1}s` }}>{s}</span>
              ))}
            </div>
          </>
        )}
      </div>

      {showSub && (
        <div className="landing-sub-panel">
          <p className="landing-sub-text">
            Celebrating the brilliant minds who power Xenovex.<br />
            Excellence &nbsp;·&nbsp; Brilliance &nbsp;·&nbsp;{" "}
            <span className="txt-green txt-bold">YOU</span>
          </p>

          <div className="landing-cards">
            <div className="landing-card">
              <span className="landing-card__icon">⭐</span>
              <span className="landing-card__label">Exciting<br />Awards</span>
            </div>
            <div className="landing-card">
              <span className="landing-card__icon">🎉</span>
              <span className="landing-card__label">Exclusive<br />Event</span>
            </div>
            <div className="landing-card">
              <span className="landing-card__icon">🎁</span>
              <span className="landing-card__label">Amazing<br />Rewards</span>
            </div>
          </div>

          <p className="landing-guess-text">
            Can you guess the{" "}
            <span className="txt-green">Venue</span> &amp;{" "}
            <span className="txt-green">Date</span>?
          </p>

          <div className="landing-cta">
            <button className="xen-btn">
              <span>BEGIN THE REVEAL</span>
              <span className="xen-btn__arrow">⟶</span>
            </button>
            <p className="landing-hint" style={{position: 'relative', bottom: 0, transform: 'none'}}>
              tap anywhere · 2 reveals await
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SCREEN 1 — VENUE REVEAL
═══════════════════════════════════════════ */
function VenueScreen({ onNext, onBack }) {
  const [phase, setPhase] = useState("enter");
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("show"),  400);
    const t2 = setTimeout(() => setPhase("ready"), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const line1 = "XENOVEX";
  const line2 = "MAIN OFFICE";

  return (
    <div className="fs-screen fs-venue" onClick={phase === "ready" ? onNext : undefined}>
      <BackBtn onClick={onBack} />
      <ProgressDots current={1} />
      <XenovexLogo className="screen-logo" />
      
      {/* Xen Star Header Component - Delayed reveal */}
      <div className={`delayed-hero ${phase === "ready" ? "delayed-hero--visible" : ""}`}>
        <XenStarHero size="large" />
      </div>

      <div className={`venue-pin ${phase !== "enter" ? "venue-pin--visible" : ""}`}>
        <div className="venue-pin__ring venue-pin__ring--1" />
        <div className="venue-pin__ring venue-pin__ring--2" />
        <div className="venue-pin__ring venue-pin__ring--3" />
        <span className="venue-pin__icon">📍</span>
      </div>

      <p className={`fs-label ${phase !== "enter" ? "fs-label--visible" : ""}`}>
        THE VENUE IS FIXED
      </p>

      <div className={`venue-name ${phase === "show" || phase === "ready" ? "venue-name--visible" : ""}`}>
        <div className="venue-name__row">
          {line1.split("").map((ch, i) => (
            <span key={i} className="venue-letter venue-letter--green"
              style={{ animationDelay: `${i * 0.07}s` }}>{ch}</span>
          ))}
        </div>
        <div className="venue-name__row">
          {line2.split("").map((ch, i) => (
            <span key={i} className="venue-letter venue-letter--white"
              style={{ animationDelay: `${(line1.length + i) * 0.07}s` }}>
              {ch === " " ? "\u00A0" : ch}
            </span>
          ))}
        </div>
      </div>

      <p className={`fs-sub ${phase === "ready" ? "fs-sub--visible" : ""}`}>
        Right where legends are made —{" "}
        <span className="txt-green">our home.</span>
      </p>

      {phase === "ready" && (
        <div className="fs-tap-hint">
          <span className="tap-pulse" /> tap anywhere to continue
        </div>
      )}

      <div className="venue-deco venue-deco--l" />
      <div className="venue-deco venue-deco--r" />
    </div>
  );
}

/* ═══════════════════════════════════════════
   SCREEN 2 — DATE REVEAL 
═══════════════════════════════════════════ */
function DateScreen({ onBack }) {
  const [phase, setPhase] = useState("enter");
  const [showInfo, setShowInfo] = useState(false);
  const { canvasRef, launch } = useConfetti();

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("show"),  400);
    const t2 = setTimeout(() => setPhase("ready"), 1800);
    const t3 = setTimeout(() => { launch(); setShowInfo(true); }, 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [launch]);

  return (
    <div className="fs-screen fs-date has-bottom-panel">
      <canvas ref={canvasRef} className="confetti-canvas" />
      <BackBtn onClick={onBack} />
      <ProgressDots current={2} />
      <XenovexLogo className="screen-logo" />
      
      {/* Xen Star Header Component - Delayed reveal */}
      <div className={`delayed-hero ${showInfo ? "delayed-hero--visible" : ""}`}>
        <XenStarHero size="large" />
      </div>

      <div className="date-curtain date-curtain--l" />
      <div className="date-curtain date-curtain--r" />
      <div className={`date-spotlight ${phase !== "enter" ? "date-spotlight--on" : ""}`} />

      <p className={`fs-label green ${phase !== "enter" ? "fs-label--visible" : ""}`}>
        MARK YOUR CALENDARS
      </p>

      <div className={`date-orb ${phase !== "enter" ? "date-orb--visible" : ""} ${phase === "ready" ? "date-orb--celebrate" : ""}`}>
        <div className="date-ring date-ring--outer" />
        <div className="date-ring date-ring--inner" />
        {[...Array(8)].map((_, i) => (
          <div key={i} className="date-orbit-dot" style={{ "--i": i }} />
        ))}
        <div className="date-content">
          <span className="date-month">APRIL</span>
          <span className="date-day">10</span>
          <span className="date-year">2026</span>
        </div>
      </div>

      <div className="date-particles">
        {[...Array(28)].map((_, i) => (
          <div key={i} className="date-particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${Math.random() * 4 + 1}px`,
            height: `${Math.random() * 4 + 1}px`,
            animationDelay: `${Math.random() * 8}s`,
            animationDuration: `${10 + Math.random() * 15}s`,
          }} />
        ))}
      </div>

      <div className={`date-glow-beam ${phase !== "enter" ? "date-glow-beam--on" : ""}`} />

      {showInfo && (
        <div className="date-info-panel">
          <p className="date-info-sub">
            <span className="txt-green">Friday, 10th April 2026</span> — the night of our Stars ⭐.
          </p>
          <div className="date-chips">
            {/* ✨ UPDATED: Glowing highlighted venue chip ✨ */}
            <span className="date-chip date-chip--highlight">
              📍 XENOVEX MAIN OFFICE
            </span>
          </div>
          <div className="date-highlights">
            <div className="date-hi-item"><span>⭐</span><span>Exciting Awards</span></div>
            <div className="date-hi-item"><span>🎉</span><span>Exclusive Event</span></div>
            <div className="date-hi-item"><span>🎁</span><span>Amazing Rewards</span></div>
          </div>
          <p className="date-tuned">
            🌟 Stay tuned for further updates — the best is yet to come! 🌟
          </p>
          <div className="date-badge">XENOVEX · XEN STAR 2026</div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   ROOT APP  
═══════════════════════════════════════════ */
export default function XenStarAwards() {
  const [screen, setScreen] = useState(0);
  const { particles, burst } = useBurst();

  const advance = (e) => {
    if (e) {
      const rect = e.currentTarget.getBoundingClientRect();
      burst(
        ((e.clientX - rect.left) / rect.width) * 100,
        ((e.clientY - rect.top)  / rect.height) * 100
      );
    }
    setScreen((s) => Math.min(s + 1, 2));
  };

  const goBack = () => setScreen((s) => Math.max(s - 1, 0));

  return (
    <div className="xen-root"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/Xen-Star-BG.webp)` }}>
      <div className="xen-starfield" aria-hidden="true">
        {STARS.map((s) => (
          <span key={s.id} className="xen-star" style={{
            left: `${s.x}%`, top: `${s.y}%`,
            width: s.size, height: s.size,
            animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s`,
          }} />
        ))}
      </div>
      <div className="xen-orb xen-orb--1" />
      <div className="xen-orb xen-orb--2" />
      <div className="xen-orb xen-orb--3" />
      <div className="xen-scanline" />

      {particles.map((p) => (
        <div key={p.id} className="xen-particle" style={{
          left: `${p.x}%`, top: `${p.y}%`,
          background: p.color, width: p.size, height: p.size,
          "--pvx": `${p.vx}px`, "--pvy": `${p.vy}px`,
        }} />
      ))}

      {screen === 0 && <LandingScreen onStart={advance} />}
      {screen === 1 && <VenueScreen   onNext={advance} onBack={goBack} />}
      {screen === 2 && <DateScreen    onBack={goBack} />}
    </div>
  );
}